import React from 'react';
import {
	FormattedMessage,
	defineMessages,
	useIntl,
} from 'react-intl';

import useMenuExpandedState from '~/hooks/useMenuExpandedState';
import useViewportType from '~/hooks/useViewportType';
import {
	isPhase3,
} from '~/utilities/viewportSize';



const messages = defineMessages({
	minimize: {
		id: 'ui.navigation.button.minimize',
		defaultMessage: 'Minimize',
	},
	keepOpen: {
		id: 'ui.navigation.button.keepOpen',
		defaultMessage: 'Keep open',
	},
});

// Single chevron path (left-pointing) - duplicated for double-arrow « »
const CHEVRON_LEFT_PATH = 'M160.085 542.781l310.856 310.25c21.486 21.445 51.426 22.731 72.685 1.288 21.485-21.445 17.803-41.104-3.683-62.318l-287.97-288 288.429-288c21.486-21.443 21.486-44.581 0-65.798-21.485-21.444-47.973-16.678-69.229 4.766l-310.856 310.251c-21.716 21.443-21.718 56.118-0.232 77.562z';
const CHEVRON_WIDTH = 544;
const CHEVRON_HEIGHT = 1024;
// Offset for second chevron - smaller = tighter spacing (almost nested)
const CHEVRON_OFFSET = 300;
const DOUBLE_CHEVRON_VIEWBOX = `0 0 ${CHEVRON_OFFSET + CHEVRON_WIDTH} ${CHEVRON_HEIGHT}`;

/**
 * Double Chevron Left Icon « - used for "Minimize" state (when navbar is expanded/Phase 3)
 * Two chevrons side by side, tightly spaced, both pointing left
 */
const DoubleChevronLeftIcon: React.FC<{ color?: string }> = ({ color = '#89c32e' }) => (
	<svg
		fill="none"
		height="20"
		viewBox={DOUBLE_CHEVRON_VIEWBOX}
		width="20"
		xmlns="http://www.w3.org/2000/svg"
	>
		{/* First chevron (left) - full size */}
		<path
			d={CHEVRON_LEFT_PATH}
			fill={color}
			fillRule="evenodd"
		/>
		{/* Second chevron (right) - full size, translated with tight spacing */}
		<g transform={`translate(${CHEVRON_OFFSET}, 0)`}>
			<path
				d={CHEVRON_LEFT_PATH}
				fill={color}
				fillRule="evenodd"
			/>
		</g>
	</svg>
);

/**
 * Double Chevron Right Icon » - used for "Keep open" state (when navbar is minimized/Phase 2)
 * Two chevrons side by side, tightly spaced, both pointing right (mirrored from left)
 */
const DoubleChevronRightIcon: React.FC<{ color?: string }> = ({ color = '#89c32e' }) => (
	<svg
		fill="none"
		height="20"
		viewBox={DOUBLE_CHEVRON_VIEWBOX}
		width="20"
		xmlns="http://www.w3.org/2000/svg"
	>
		<g transform={`translate(${(CHEVRON_OFFSET + CHEVRON_WIDTH) / 2}, ${CHEVRON_HEIGHT / 2}) scale(-1, 1) translate(-${(CHEVRON_OFFSET + CHEVRON_WIDTH) / 2}, -${CHEVRON_HEIGHT / 2})`}>
			{/* First chevron (left) - full size */}
			<path
				d={CHEVRON_LEFT_PATH}
				fill={color}
				fillRule="evenodd"
			/>
			{/* Second chevron (right) - full size, translated with tight spacing */}
			<g transform={`translate(${CHEVRON_OFFSET}, 0)`}>
				<path
					d={CHEVRON_LEFT_PATH}
					fill={color}
					fillRule="evenodd"
				/>
			</g>
		</g>
	</svg>
);

/**
 * Navigation Minimize Toggle Component
 *
 * Button that allows users to manually control navbar expansion state.
 *
 * Behavior:
 * - When navbar is expanded (Phase 3, either naturally or via menu expansion):
 *   - Shows "Minimize" label with left arrow icon
 *   - Clicking collapses to Phase 2 (only works if menu was expanded via click)
 * - When navbar is collapsed (Phase 2):
 *   - Shows "Keep open" label with right arrow icon (only visible in Phase 3)
 *   - Clicking expands to Phase 3
 *
 * Note: In naturally Phase 3 (viewport ≥1169px), the button allows minimizing
 * to Phase 2 even on large screens, giving users manual control.
 */
const NavigationMinimizeToggle: React.FC = () => {
	const intl = useIntl();
	const viewportType = useViewportType();
	const [
		isMenuExpanded,
		setMenuExpanded,
		isMenuMinimized,
		setMenuMinimized,
	] = useMenuExpandedState();

	// Check if we're in Phase 3 (either naturally or via menu expansion)
	const effectivePhase3 = isPhase3(viewportType, isMenuExpanded, isMenuMinimized);
	const isNaturallyPhase3 = isPhase3(viewportType);

	// Determine button state based on effective phase
	const isExpanded = effectivePhase3;

	// Choose icon and text based on current state
	const buttonIcon = isExpanded
		? <DoubleChevronLeftIcon />
		: <DoubleChevronRightIcon />;
	const buttonText = isExpanded
		? <FormattedMessage {...messages.minimize} />
		: <FormattedMessage {...messages.keepOpen} />;

	const handleClick = React.useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();

			if (isExpanded) {
				// Currently expanded: collapse to Phase 2
				// If naturally Phase 3, use minimized state; otherwise clear expansion
				if (isNaturallyPhase3) {
					setMenuMinimized(true);
				} else {
					setMenuExpanded(false);
				}
			} else {
				// Currently collapsed: expand to Phase 3
				// Clear minimized state and set expansion
				setMenuMinimized(false);
				setMenuExpanded(true);
			}
		},
		[isExpanded, isNaturallyPhase3, setMenuExpanded, setMenuMinimized],
	);

	return (
		<button
			aria-label={intl.formatMessage(isExpanded ? messages.minimize : messages.keepOpen)}
			className="navigation-minimize-toggle-button"
			onClick={handleClick}
			type="button"
		>
			<span className="navigation-minimize-toggle-label">
				{buttonText}
			</span>
			<span className="navigation-minimize-toggle-icon">
				{buttonIcon}
			</span>
		</button>
	);
};

export default NavigationMinimizeToggle;
